<template>
  <div class="min-vh main-theme">
    <div class="d-flex justify-content-between align-items-center p-3 p-sm-0">
      <h1 class="mb-0 header-main text-uppercase d-inline">
        {{ isEdit ? "EDIT" : "CREATE" }} WHATSAPP TEMPLATE
      </h1>
      <b-badge
        v-if="isEdit && original.status"
        :variant="statusInfo(original.status).variant"
        class="status-badge"
        >{{ statusInfo(original.status).label }}</b-badge
      >
    </div>

    <b-alert
      :show="isEdit && original.status === 'REJECTED'"
      variant="danger"
      class="mt-3 mb-0"
    >
      WhatsApp ไม่อนุมัติ Template นี้
      <span v-if="original.rejectedReason && original.rejectedReason !== 'NONE'"
        >(เหตุผล: {{ original.rejectedReason }})</span
      >
      แก้ไขแล้วกดบันทึกเพื่อส่งตรวจใหม่
    </b-alert>

    <b-row class="mt-3" v-if="!isLoading">
      <!-- form -->
      <b-col lg="7">
        <div class="bg-white-border p-3 mb-3">
          <div class="section-title">ข้อมูลทั่วไป</div>
          <b-form-group
            label="ชื่อ Template *"
            :invalid-feedback="errors.name"
            :state="errors.name ? false : null"
            description="ใช้ได้เฉพาะ a-z ตัวเลข และ _ (เปลี่ยนชื่อภายหลังไม่ได้)"
          >
            <b-form-input
              v-model="form.name"
              :readonly="isEdit"
              placeholder="เช่น order_update"
              :formatter="formatName"
              :state="errors.name ? false : null"
            />
          </b-form-group>

          <b-form-group label="ภาษา *">
            <b-form-select
              v-model="form.language"
              :options="languageOptions"
              :disabled="isEdit"
            />
          </b-form-group>

          <b-form-group label="หมวดหมู่ *" class="mb-0">
            <div class="d-flex flex-wrap category-list">
              <div
                v-for="item in categoryOptions"
                :key="item.value"
                class="category-card"
                :class="{
                  active: form.category === item.value,
                  disabled: isCategoryLocked
                }"
                @click="!isCategoryLocked && (form.category = item.value)"
              >
                <div class="font-weight-bold">{{ item.label }}</div>
                <div class="small text-muted">{{ item.note }}</div>
              </div>
            </div>
            <div v-if="isCategoryLocked" class="small text-muted mt-1">
              Template ที่อนุมัติแล้วเปลี่ยนหมวดหมู่ไม่ได้
            </div>
            <b-alert
              :show="form.category === 'MARKETING'"
              variant="warning"
              class="small mt-2 mb-0 py-2"
            >
              WhatsApp จำกัดจำนวน Template การตลาดที่ลูกค้าแต่ละคนได้รับ
              ถ้าใช้ตอบลูกค้าที่เกิน 24 ชม. บ่อย ๆ อาจส่งไม่ถึง
              เรื่องที่เกี่ยวกับออเดอร์หรือเคสของลูกค้าให้เลือก Utility
            </b-alert>
          </b-form-group>
        </div>

        <!-- header -->
        <div class="bg-white-border p-3 mb-3">
          <div class="section-title">
            หัวข้อ <span class="text-muted small">(ไม่บังคับ)</span>
          </div>
          <b-form-group
            :invalid-feedback="errors.header"
            :state="errors.header ? false : null"
            :description="`${form.header.length}/60 ตัวอักษร ใส่ตัวแปรได้ 1 ตัว`"
          >
            <b-input-group>
              <b-form-input
                v-model="form.header"
                maxlength="60"
                placeholder="เช่น อัปเดตออเดอร์ของคุณ"
                :state="errors.header ? false : null"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="headerVariableCount >= 1"
                  @click="addVariable('header')"
                  >+ ตัวแปร</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-input-group
            v-if="headerVariableCount"
            size="sm"
            :prepend="'ตัวอย่าง {' + '{1}' + '}'"
          >
            <b-form-input
              v-model="form.headerExample"
              placeholder="ค่าตัวอย่างให้ WhatsApp ใช้ตรวจ"
              :state="errors.headerExample ? false : null"
            />
          </b-input-group>
        </div>

        <!-- body -->
        <div class="bg-white-border p-3 mb-3">
          <div class="section-title">เนื้อหา *</div>
          <b-form-group
            :invalid-feedback="errors.body"
            :state="errors.body ? false : null"
            :description="`${form.body.length}/1024 ตัวอักษร`"
            class="mb-2"
          >
            <b-form-textarea
              ref="bodyInput"
              v-model="form.body"
              rows="8"
              maxlength="1024"
              :placeholder="bodyPlaceholder"
              :state="errors.body ? false : null"
            />
          </b-form-group>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mb-2"
            @click="addVariable('body')"
            >+ เพิ่มตัวแปร</b-button
          >
          <div class="small text-muted mb-2">
            ตัวแปร <code v-pre>{{1}}</code>, <code v-pre>{{2}}</code> ...
            คือส่วนที่ agent กรอกตอนส่ง (เช่นชื่อลูกค้า เลขออเดอร์)
            ต้องใส่ค่าตัวอย่างทุกตัว
          </div>
          <b-input-group
            v-for="(value, index) in form.bodyExamples"
            :key="'example' + index"
            size="sm"
            class="mb-1"
            :prepend="`ตัวอย่าง {{${index + 1}}}`"
          >
            <b-form-input
              v-model="form.bodyExamples[index]"
              placeholder="ค่าตัวอย่าง"
              :state="errors.bodyExamples && !value.trim() ? false : null"
            />
          </b-input-group>
          <div v-if="errors.bodyExamples" class="small text-danger">
            {{ errors.bodyExamples }}
          </div>
          <div v-for="tip in bodyTips" :key="tip" class="small text-warning">
            <font-awesome-icon icon="exclamation-circle" class="mr-1" />{{
              tip
            }}
          </div>
        </div>

        <!-- footer -->
        <div class="bg-white-border p-3 mb-3">
          <div class="section-title">
            ท้ายข้อความ <span class="text-muted small">(ไม่บังคับ)</span>
          </div>
          <b-form-group
            class="mb-0"
            :description="`${form.footer.length}/60 ตัวอักษร ใส่ตัวแปรไม่ได้`"
            :invalid-feedback="errors.footer"
            :state="errors.footer ? false : null"
          >
            <b-form-input
              v-model="form.footer"
              maxlength="60"
              placeholder="เช่น ตอบกลับข้อความนี้เพื่อคุยกับเจ้าหน้าที่"
              :state="errors.footer ? false : null"
            />
          </b-form-group>
        </div>

        <!-- buttons -->
        <div class="bg-white-border p-3 mb-3">
          <div class="section-title">
            ปุ่ม <span class="text-muted small">(ไม่บังคับ สูงสุด 10 ปุ่ม)</span>
          </div>
          <div
            v-for="(button, index) in form.buttons"
            :key="'button' + index"
            class="button-row"
          >
            <b-row class="no-gutters align-items-start">
              <b-col cols="12" md="3" class="pr-md-2 mb-1">
                <b-form-select
                  v-model="button.type"
                  size="sm"
                  :options="buttonTypeOptions"
                />
              </b-col>
              <b-col cols="12" md="4" class="pr-md-2 mb-1">
                <b-form-input
                  v-model="button.text"
                  size="sm"
                  maxlength="25"
                  placeholder="ข้อความบนปุ่ม"
                />
              </b-col>
              <b-col cols="10" md="4" class="pr-2 mb-1">
                <b-form-input
                  v-if="button.type === 'URL'"
                  v-model="button.url"
                  size="sm"
                  placeholder="https://..."
                />
                <b-form-input
                  v-else-if="button.type === 'PHONE_NUMBER'"
                  v-model="button.phoneNumber"
                  size="sm"
                  placeholder="+66812345678"
                />
              </b-col>
              <b-col cols="2" md="1" class="text-right">
                <b-button
                  size="sm"
                  variant="link"
                  class="text-danger"
                  @click="form.buttons.splice(index, 1)"
                >
                  <font-awesome-icon icon="trash-alt" />
                </b-button>
              </b-col>
            </b-row>
          </div>
          <div v-if="errors.buttons" class="small text-danger mb-1">
            {{ errors.buttons }}
          </div>
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="form.buttons.length >= 10"
            @click="addButton"
            >+ เพิ่มปุ่ม</b-button
          >
        </div>
      </b-col>

      <!-- preview -->
      <b-col lg="5">
        <div class="preview-sticky">
          <div class="bg-white-border p-3">
            <div class="section-title">ตัวอย่างที่ลูกค้าจะเห็น</div>
            <div class="wa-preview">
              <div class="wa-bubble">
                <div v-if="form.header" class="font-weight-bold mb-1">
                  {{ renderPreview(form.header, [form.headerExample]) }}
                </div>
                <div class="wa-body">
                  {{
                    form.body
                      ? renderPreview(form.body, form.bodyExamples)
                      : "เนื้อหาข้อความ"
                  }}
                </div>
                <div v-if="form.footer" class="wa-footer">
                  {{ form.footer }}
                </div>
              </div>
              <div
                v-for="(button, index) in form.buttons"
                :key="'preview' + index"
                class="wa-button"
              >
                {{ button.text || "ปุ่ม" }}
              </div>
            </div>
            <div class="small text-muted mt-2">
              หลังบันทึก WhatsApp จะตรวจ Template ก่อนใช้งานได้
              (ปกติไม่กี่นาทีถึง 24 ชม.)
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <div class="d-flex justify-content-between mt-2" v-if="!isLoading">
      <b-button @click="onBack()" class="btn-cancel text-uppercase px-5"
        >CANCEL</b-button
      >
      <button
        type="button"
        @click="onSave"
        :disabled="isSaving"
        class="btn btn-main btn-details-set text-uppercase"
      >
        {{ isEdit ? "SAVE & SUBMIT" : "SUBMIT FOR REVIEW" }}
      </button>
    </div>
  </div>
</template>

<script>
const VARIABLE_REGEX = /{{\s*(\d+)\s*}}/g;

const STATUS_INFO = {
  APPROVED: { label: "อนุมัติแล้ว", variant: "success" },
  PENDING: { label: "รอตรวจ", variant: "warning" },
  IN_APPEAL: { label: "กำลังอุทธรณ์", variant: "warning" },
  REJECTED: { label: "ไม่อนุมัติ", variant: "danger" },
  PAUSED: { label: "ถูกพักใช้", variant: "secondary" },
  DISABLED: { label: "ถูกปิดใช้", variant: "dark" },
};

const variableNumbers = (text) =>
  [...(text || "").matchAll(VARIABLE_REGEX)].map((m) => Number(m[1]));

export default {
  name: "WhatsAppTemplateDetail",
  data: function () {
    return {
      isLoading: false,
      isSaving: false,
      id: this.$route.params.id,
      original: {},
      form: {
        name: "",
        language: "th",
        category: "UTILITY",
        header: "",
        headerExample: "",
        body: "",
        bodyExamples: [],
        footer: "",
        buttons: [],
      },
      errors: {},
      bodyPlaceholder: "เช่น สวัสดีคุณ {{1}} ออเดอร์ {{2}} ของคุณจัดส่งแล้ว",
      languageOptions: [
        { value: "th", text: "ไทย (th)" },
        { value: "en_US", text: "English US (en_US)" },
        { value: "en", text: "English (en)" },
      ],
      categoryOptions: [
        {
          value: "UTILITY",
          label: "Utility",
          note: "แจ้งเรื่องที่ลูกค้าทำค้างไว้ เช่น ออเดอร์ นัดหมาย ติดตามเคส",
        },
        {
          value: "MARKETING",
          label: "Marketing",
          note: "โปรโมชัน ข่าวสาร ชวนกลับมาซื้อ (จำกัดจำนวนต่อลูกค้า)",
        },
      ],
      buttonTypeOptions: [
        { value: "QUICK_REPLY", text: "ตอบกลับด่วน" },
        { value: "URL", text: "เปิดลิงก์" },
        { value: "PHONE_NUMBER", text: "โทรออก" },
      ],
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
    isCategoryLocked() {
      return this.isEdit && this.original.status === "APPROVED";
    },
    headerVariableCount() {
      return variableNumbers(this.form.header).length;
    },
    bodyVariableCount() {
      const numbers = variableNumbers(this.form.body);
      return numbers.length ? Math.max(...numbers) : 0;
    },
    // ข้อควรระวังที่ทำให้ WhatsApp มักไม่อนุมัติ
    bodyTips() {
      const tips = [];
      const body = this.form.body.trim();
      if (/^{{\s*\d+\s*}}/.test(body) || /{{\s*\d+\s*}}[.!?]?$/.test(body))
        tips.push("ไม่ควรขึ้นต้นหรือลงท้ายเนื้อหาด้วยตัวแปร");
      const words = body.replace(VARIABLE_REGEX, "").trim().split(/\s+/);
      if (this.bodyVariableCount && words.length < this.bodyVariableCount * 3)
        tips.push("ตัวแปรเยอะเมื่อเทียบกับข้อความ ควรเพิ่มข้อความประกอบ");
      return tips;
    },
  },
  watch: {
    // จำนวนช่องตัวอย่างเท่ากับตัวแปรสูงสุดในเนื้อหาเสมอ
    bodyVariableCount: {
      immediate: true,
      handler(count) {
        const examples = this.form.bodyExamples.slice(0, count);
        while (examples.length < count) examples.push("");
        this.form.bodyExamples = examples;
      },
    },
  },
  created: async function () {
    if (!this.isEdit) return;
    this.isLoading = true;
    await this.$store.commit("UPDATE_MAIN_LOADING", true);
    await this.getDetail();
    this.isLoading = false;
    await this.$store.commit("UPDATE_MAIN_LOADING", false);
  },
  methods: {
    statusInfo(status) {
      return STATUS_INFO[status] || { label: status, variant: "secondary" };
    },
    formatName(value) {
      return (value || "")
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
    },
    renderPreview(text, values) {
      return (text || "").replace(VARIABLE_REGEX, (match, n) => {
        const value = values[Number(n) - 1];
        return value && value.trim() ? value : match;
      });
    },
    onBack() {
      this.$router.push({
        path: `/whatsapp-template`,
        query: { whatsappId: this.$route.query.whatsappId },
      });
    },
    addVariable(target) {
      if (target === "header") {
        this.form.header = `${this.form.header} {{1}}`.trim();
        return;
      }
      const next = this.bodyVariableCount + 1;
      const textarea = this.$refs.bodyInput && this.$refs.bodyInput.$el;
      const position = textarea
        ? textarea.selectionStart
        : this.form.body.length;
      const token = `{{${next}}}`;
      this.form.body =
        this.form.body.slice(0, position) +
        token +
        this.form.body.slice(position);
      this.$nextTick(() => {
        if (!textarea) return;
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd =
          position + token.length;
      });
    },
    addButton() {
      this.form.buttons.push({
        type: "QUICK_REPLY",
        text: "",
        url: "",
        phoneNumber: "",
      });
    },
    async getDetail() {
      const data = await this.axios
        .get(
          `${this.$baseUrl}/whatsapp/GetTemplate/${this.$route.query.whatsappId}/${this.id}`
        )
        .catch(() => null);
      if (!data || data.result != 1 || !data.detail) {
        this.handleFail((data && data.message) || "ไม่พบ Template");
        return;
      }
      const template = data.detail;
      this.original = template;
      const components = template.components || [];
      const find = (type) => components.find((c) => c.type === type) || {};
      const header = find("HEADER");
      const body = find("BODY");
      const footer = find("FOOTER");
      const buttons = find("BUTTONS").buttons || [];
      const bodyExamples =
        (body.example && (body.example.bodyText || [])[0]) || [];

      this.form.name = template.name;
      this.form.language = template.language;
      this.form.category = template.category;
      this.form.header =
        !header.format || header.format === "TEXT" ? header.text || "" : "";
      this.form.headerExample =
        (header.example && (header.example.headerText || [])[0]) || "";
      this.form.body = body.text || "";
      this.form.footer = footer.text || "";
      this.form.buttons = buttons.map((b) => ({
        type: b.type,
        text: b.text || "",
        url: b.url || "",
        phoneNumber: b.phoneNumber || "",
      }));
      // รอ watcher สร้างช่องตัวอย่างตามจำนวนตัวแปรก่อน แล้วค่อยใส่ค่าเดิม
      this.$nextTick(() => {
        this.form.bodyExamples = this.form.bodyExamples.map(
          (v, i) => bodyExamples[i] || ""
        );
      });
    },
    validate() {
      const errors = {};
      const f = this.form;
      if (!f.name) errors.name = "กรุณาระบุชื่อ Template";
      else if (!/^[a-z0-9_]+$/.test(f.name))
        errors.name = "ใช้ได้เฉพาะ a-z ตัวเลข และ _";

      if (this.headerVariableCount > 1)
        errors.header = "หัวข้อใส่ตัวแปรได้ 1 ตัว";
      else if (variableNumbers(f.header).some((n) => n !== 1))
        errors.header = "ตัวแปรในหัวข้อต้องเป็น {{1}}";
      if (this.headerVariableCount && !f.headerExample.trim())
        errors.headerExample = "กรุณาใส่ค่าตัวอย่าง";

      if (!f.body.trim()) errors.body = "กรุณาใส่เนื้อหา";
      const numbers = [...new Set(variableNumbers(f.body))].sort(
        (a, b) => a - b
      );
      if (numbers.some((n, i) => n !== i + 1))
        errors.body =
          "ตัวแปรต้องเรียงต่อกัน {{1}}, {{2}}, {{3}} ... ห้ามข้ามเลข";
      if (f.bodyExamples.some((v) => !v.trim()))
        errors.bodyExamples = "กรุณาใส่ค่าตัวอย่างของตัวแปรให้ครบ";

      if (/{{.*}}/.test(f.footer)) errors.footer = "ท้ายข้อความใส่ตัวแปรไม่ได้";

      const badButton = f.buttons.find(
        (b) =>
          !b.text.trim() ||
          (b.type === "URL" && !/^https?:\/\/\S+$/.test(b.url.trim())) ||
          (b.type === "PHONE_NUMBER" &&
            !/^\+?\d{8,15}$/.test(b.phoneNumber.trim()))
      );
      if (badButton)
        errors.buttons =
          "กรุณากรอกข้อความบนปุ่ม ลิงก์ (ขึ้นต้น https://) หรือเบอร์โทร (เช่น +66812345678) ให้ถูกต้อง";

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    // แปลงฟอร์มเป็น components ตามรูปแบบของ WhatsApp
    // (ชื่อ field เป็น camelCase ให้ BackOfficeAPI bind ได้ แล้ว API แปลงเป็น snake_case ตอนส่งให้ Meta)
    buildComponents() {
      const f = this.form;
      const components = [];
      if (f.header.trim()) {
        const header = {
          type: "HEADER",
          format: "TEXT",
          text: f.header.trim(),
        };
        if (this.headerVariableCount)
          header.example = { headerText: [f.headerExample.trim()] };
        components.push(header);
      }
      const body = { type: "BODY", text: f.body.trim() };
      if (f.bodyExamples.length)
        body.example = { bodyText: [f.bodyExamples.map((v) => v.trim())] };
      components.push(body);
      if (f.footer.trim())
        components.push({ type: "FOOTER", text: f.footer.trim() });
      if (f.buttons.length) {
        components.push({
          type: "BUTTONS",
          buttons: f.buttons.map((b) => {
            const button = { type: b.type, text: b.text.trim() };
            if (b.type === "URL") button.url = b.url.trim();
            if (b.type === "PHONE_NUMBER")
              button.phoneNumber = b.phoneNumber.trim();
            return button;
          }),
        });
      }
      return components;
    },
    async onSave() {
      if (!this.validate()) return;
      const whatsappId = this.$route.query.whatsappId;
      const payload = {
        name: this.form.name,
        language: this.form.language,
        category: this.form.category,
        status: this.original.status,
        components: this.buildComponents(),
      };
      const url = this.isEdit
        ? `${this.$baseUrl}/whatsapp/UpdateTemplate/${whatsappId}/${this.id}`
        : `${this.$baseUrl}/whatsapp/CreateTemplate/${whatsappId}`;

      this.isSaving = true;
      await this.$store.commit("UPDATE_MAIN_LOADING", true);
      await this.axios
        .post(url, payload)
        .then((data) => {
          if (data.result == 1) this.handleSuccess(data.message);
          else this.handleFail(data.message || "บันทึกไม่สำเร็จ");
        })
        .catch((err) => this.handleFail(err.message));
      this.isSaving = false;
      await this.$store.commit("UPDATE_MAIN_LOADING", false);
    },
    handleSuccess(msg) {
      this.$store.commit("UPDATE_GLOBAL_ALERT", { isShow: true, msg: msg });
      this.onBack();
    },
    handleFail(msg) {
      this.$store.commit("UPDATE_GLOBAL_ALERT_ERROR", {
        isShow: true,
        msg: msg,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.section-title {
  font-weight: bold;
  margin-bottom: 8px;
}

.status-badge {
  font-size: 14px;
  padding: 6px 10px;
}

.category-list {
  gap: 8px;
}

.category-card {
  flex: 1 1 200px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;

  &.active {
    border-color: #25d366;
    background-color: #f0fbf4;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.button-row {
  border-bottom: 1px dashed #e9e9ef;
  padding-top: 4px;
  margin-bottom: 4px;
}

.preview-sticky {
  position: sticky;
  top: 16px;
}

.wa-preview {
  background-color: #efeae2;
  border-radius: 8px;
  padding: 16px;
  min-height: 160px;
}

.wa-bubble {
  background-color: white;
  border-radius: 8px;
  padding: 8px 10px;
  max-width: 320px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
}

.wa-body {
  white-space: pre-line;
  word-break: break-word;
}

.wa-footer {
  color: #8696a0;
  font-size: 12px;
  margin-top: 4px;
}

.wa-button {
  background-color: white;
  border-radius: 8px;
  margin-top: 4px;
  padding: 6px;
  max-width: 320px;
  text-align: center;
  color: #027eb5;
}
</style>
